import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Drawer } from "antd";
import { ShopOutlined, LogoutOutlined, MenuOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { usePlatformAuth } from "../../context/PlatformAuthContext";
import { hexAlpha } from "../../utils/colorUtils";

const PLATFORM_PRIMARY = "#4f46e5";

const menuItems = [
  { key: "/platform", label: "Tiendas", icon: ShopOutlined },
  { key: "/platform/localidades", label: "Localidades", icon: EnvironmentOutlined },
];

function SidebarItem({ item, isSelected, primary, onClick }) {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;
  const bg = isSelected ? hexAlpha(primary, 0.10) : hovered ? hexAlpha(primary, 0.05) : "transparent";
  const color = isSelected ? primary : hovered ? primary : "#4b5563";
  const fontWeight = isSelected ? 600 : 400;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 16px",
        borderRadius: 8,
        margin: "2px 10px",
        cursor: "pointer",
        background: bg,
        color,
        fontWeight,
        fontSize: 14,
        transition: "background 0.15s, color 0.15s",
        position: "relative",
      }}
    >
      {isSelected && (
        <div style={{
          position: "absolute", left: 0, top: "20%", bottom: "20%",
          width: 3, borderRadius: "0 3px 3px 0",
          background: primary,
        }} />
      )}
      <Icon style={{ fontSize: 16, flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{item.label}</span>
    </div>
  );
}

export const PlatformLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { admin, logout } = usePlatformAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const primary = PLATFORM_PRIMARY;
  const selectedKey = menuItems.find(m => location.pathname === m.key)?.key || "/platform";
  const currentSection = menuItems.find(m => m.key === selectedKey)?.label || "Platform";
  const email = admin?.email || "";
  const initials = "IP";

  const handleSelect = (key) => {
    navigate(key);
    setMobileOpen(false);
  };

  const sidebarContent = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fff" }}>
      {/* Brand */}
      <div style={{
        padding: "20px 20px 16px",
        borderBottom: `1px solid ${hexAlpha(primary, 0.10)}`,
        display: "flex", alignItems: "center", gap: 12,
        cursor: "pointer",
        background: hexAlpha(primary, 0.04),
      }}
        onClick={() => navigate("/platform")}
      >
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: hexAlpha(primary, 0.14),
          color: primary,
          fontWeight: 800, fontSize: 15,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Platform
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {email || "Panel del sistema"}
          </div>
        </div>
      </div>

      {/* Menú */}
      <nav style={{ flex: 1, paddingTop: 10, overflowY: "auto" }}>
        {menuItems.map(item => (
          <SidebarItem
            key={item.key}
            item={item}
            isSelected={selectedKey === item.key}
            primary={primary}
            onClick={() => handleSelect(item.key)}
          />
        ))}
      </nav>

      {/* Footer */}
      <div style={{ padding: "12px 10px", borderTop: "1px solid #f3f4f6" }}>
        <div
          onClick={logout}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 16px", borderRadius: 8,
            cursor: "pointer", color: "#ef4444", fontSize: 14,
            transition: "background 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <LogoutOutlined style={{ fontSize: 16 }} />
          <span>Cerrar sesión</span>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f5f6fa" }}>
      {/* Sidebar desktop */}
      {!isMobile && (
        <aside style={{
          width: 240, flexShrink: 0,
          borderRight: "1px solid #f3f4f6",
          boxShadow: "2px 0 8px rgba(0,0,0,0.04)",
          position: "sticky", top: 0, height: "100vh", overflowY: "auto",
        }}>
          {sidebarContent}
        </aside>
      )}

      {/* Mobile drawer */}
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        placement="left"
        width={240}
        styles={{ body: { padding: 0 }, header: { display: "none" } }}
        title={null}
        closable={false}
      >
        {sidebarContent}
      </Drawer>

      {/* Main */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* Top header */}
        <header style={{
          background: "#fff",
          borderBottom: "1px solid #f3f4f6",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          height: 56,
          padding: "0 20px",
          display: "flex", alignItems: "center", gap: 12,
          position: "sticky", top: 0, zIndex: 100,
        }}>
          {isMobile && (
            <button
              onClick={() => setMobileOpen(true)}
              style={{
                background: "none", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 36, height: 36, borderRadius: 8,
                color: "#374151",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <MenuOutlined style={{ fontSize: 18 }} />
            </button>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
              {currentSection}
            </span>
          </div>

          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "6px 12px", borderRadius: 8,
            background: hexAlpha(primary, 0.07),
          }}>
            <div style={{
              width: 26, height: 26, borderRadius: 7,
              background: hexAlpha(primary, 0.18),
              color: primary, fontWeight: 700, fontSize: 11,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              {initials}
            </div>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>
              {email || "Platform"}
            </span>
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, padding: isMobile ? 16 : 24 }}>
          {children}
        </main>
      </div>
    </div>
  );
};
